import { Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core'
import { FormBuilder } from '@angular/forms'
import { createFieldSettingForm } from './field-setting-form/field-setting-form.component'

import * as $ from 'jquery'
import { FieldsEditorActionManagerService } from '../fields-editor/fields-editor-action-manager.service'
import { pairwise, startWith } from 'rxjs/operators'
import { FieldRendererComponent } from './field-renderer/field-renderer.component'
import { RoleColor } from './constants'
import { SignFieldDef, SignFieldDefAttributes, SignFieldRect, SignFieldType } from '@tokeet-frontend/signature'
import { Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'

export interface FieldPlaceholderData {
  role: string
  value: string
  attributes: SignFieldDefAttributes
  font: string
  font_size: number
  readonly?: boolean
}

export interface FieldPlaceholderSettings {
  role: string
  value: string
  attributes: SignFieldDefAttributes
  font: string
  font_size: number
  label: string
  required: boolean
  fill_before_sent?: boolean
  readonly?: boolean
  prefill_data_id?: string
}

let fieldPlaceholderIndex = 0

@Component({
  selector: 'app-field-placeholder',
  template: '',
})
export class FieldPlaceholderComponent extends Destroyable implements OnInit {
  type: SignFieldType
  @Input() id: string

  @Input() data: FieldPlaceholderData

  @Input() label: string
  @Input() required: boolean
  @Input() isRoleEditable: boolean = true
  @Input() disableCfg: boolean = false
  @Input() zoomScale = 1
  @Input() roleColors: RoleColor[] = []
  @Input() boundsRef: HTMLElement
  @Input() inBounds: boolean = true
  @Input() role2ColorMapping: any = {}
  @Input() isEditableGuestDataDictField = false

  @ViewChild(FieldRendererComponent) fieldRender: FieldRendererComponent

  @Output() remove: EventEmitter<any> = new EventEmitter<any>()
  @Output() roleChange: EventEmitter<any> = new EventEmitter<any>()

  form = createFieldSettingForm(this.fb)
  offsetRect: SignFieldRect

  get role() {
    return this.form && this.form.get('role').value
  }

  private currentRectSnapshot: SignFieldRect

  constructor(
    private render: Renderer2,
    private fb: FormBuilder,
    private elementRef: ElementRef,
    private actionManager: FieldsEditorActionManagerService
  ) {
    super()
  }

  ngOnInit() {
    this.form.patchValue({
      label: this.label,
      role: this.data.role,
      required: this.required,
      font: this.data.font,
      font_size: this.data.font_size,
      value: this.data.value,
      fill_before_sent: this.data.attributes?.fill_before_sent,
      prefill_data_id: this.data.attributes?.prefill_data_id,
      readonly: this.data.readonly,
    })

    setTimeout(() => {
      if (!this.id) {
        this.id = `field-${fieldPlaceholderIndex++}`
        this.actionManager.doAdd(this.id, this.getFieldConfig())
      }
      this.currentRectSnapshot = this.getFieldRect()
    }, 100)

    this.form.valueChanges
      .pipe(startWith(this.form.getRawValue()), pairwise(), untilDestroy(this))
      .subscribe(([s1, s2]: [FieldPlaceholderData, FieldPlaceholderData]) => {
        this.actionManager.doUpdateSettings(this.id, s2, s1)
      })
  }

  private getFieldSettings(): FieldPlaceholderSettings {
    const { fill_before_sent, prefill_data_id, ...others } = this.form.getRawValue()
    return {
      ...this.data,
      attributes: {
        ...this.data.attributes,
        fill_before_sent,
        prefill_data_id,
      },
      ...others,
    }
  }

  getFieldRect() {
    const element = $(this.elementRef.nativeElement)
    const draggableElement = $('[ngdraggable]', element)
    const resizableElement = $('[ngresizable]', element)

    const t1 = this.getTranslateXY(this.elementRef.nativeElement)
    const t2 = this.getTranslateXY(draggableElement[0])

    return {
      x: t1.x + t2.x + resizableElement.position().left,
      y: t1.y + t2.y + resizableElement.position().top,
      width: draggableElement.width(),
      height: draggableElement.height(),
    }
  }

  getFieldConfig(): SignFieldDef {
    const settings = this.getFieldSettings()
    const config = {
      ...settings,
      ...this.getFieldRect(),
      type: this.type,
    }
    return config
  }

  getTranslateXY(element: HTMLElement) {
    const style = window.getComputedStyle(element)
    const matrix = new DOMMatrixReadOnly(style.transform)
    return {
      x: matrix.m41 || 0,
      y: matrix.m42 || 0,
    }
  }

  isPositionWillChanged(rect: SignFieldRect) {
    if (rect.x === this.currentRectSnapshot.x && rect.y === this.currentRectSnapshot.y) {
      return false // no position change
    }
    return true
  }

  isSizeWillChanged(rect: SignFieldRect) {
    if (rect.width === this.currentRectSnapshot.width && rect.height === this.currentRectSnapshot.height) {
      return false // no size change
    }
    return true
  }

  onMove() {
    const newRect = this.getFieldRect()
    if (!this.isPositionWillChanged(newRect)) {
      // prevent unless move action from draggable directive.
      return
    }
    this.actionManager.doMove(this.id, newRect, this.currentRectSnapshot)
    this.setRect(newRect)
  }

  onResize() {
    const newRect = this.getFieldRect()
    if (this.isSizeWillChanged(newRect)) {
      return // no size change
    }
    this.actionManager.doResize(this.id, newRect, this.currentRectSnapshot)
    this.setRect(newRect)
  }

  doMove(rect: SignFieldRect) {
    this.setRect(rect)
  }

  doResize(rect: SignFieldRect) {
    this.setRect(rect)
  }

  setSettings(settings) {
    this.form.patchValue(settings, { emitEvent: false })
    this.fieldRender.refreshRoleColor()
  }

  setRect(data: SignFieldRect) {
    this.currentRectSnapshot = data
    this.offsetRect = {
      // this will reset child transform applied by draggable and resizable
      x: 0,
      y: 0,
      width: data.width,
      height: data.height,
    }
    // set z axis to show it on top;
    this.render.setStyle(this.elementRef.nativeElement, 'transform', `translate3d(${data.x}px, ${data.y}px, 1000px)`)
  }

  onRemove($event) {
    this.actionManager.doRemove(this.id, this.getFieldConfig())
    this.remove.emit()
  }

  onRoleChange($event) {
    this.roleChange.emit(this.role)
  }
}
